/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core public entry point.
//
// createPcgCore(payload, options) builds an immutable analysis context around one
// guide payload (the prototype's `D` global) and returns the derived-result API.
// The context replaces four prototype globals:
//   D                              -> ctx.payload
//   MURMUR (decoded trace)         -> ctx.murmurTrace
//   TURBULENT_RF_MODEL             -> ctx.turbulentModel
//   phaseReviewResolution          -> ctx.phaseReviewResolution (fixed per context)
// plus the exam-overlay window globals (__medaicaExamTimingConsistency,
// __medaicaCurrentRecordingId) -> ctx.examTimingConsistency / ctx.currentRecordingId.
//
// Memoization note: analysisQuality caches into ctx.cache. Because every input the
// memo depends on is fixed at context creation, the prototype's stale-memo bug
// (D._analysisQuality surviving a phase-review resolution) cannot occur here —
// resolve a phase review by creating a new context with the new resolution.
//
// Timing engine (upstream v0.9.1 m1acq): a payload carrying `mvp2Timing` is
// split into a metric scope and a visual scope (evaluation-range.js). The
// context's `payload` is the metric scope, so every derived result comes from
// the Evaluation Range; `visualPayload` (the whole recording's markers) is what
// the canvas draws; `guidePayload` is the payload as given; `engine` is the
// engine's state and `evaluationRange` the selected range when it is ready
// (null otherwise — the gate for the Evaluation Range presentation rules).

import { decodeAnalysisTraces } from "./traces"
import { analysisQuality } from "./analysis-quality"
import {
  timingStats,
  timingConsistency,
  rhythmPatternFromTiming,
  crossPositionTimingReview,
  crossPositionTimingCorrection,
} from "./timing"
import {
  visualAcousticActivityStats,
  murmurEvidenceStats,
  structuredTurbulenceEvidence,
  nrpeExperimentSchema,
} from "./evidence"
import { phaseRiskConfidence, phaseReviewNeeded, phaseAlternateActivity } from "./phase"
import { buildCardiacReportEvidencePackage } from "./export-package"
import { intervalAt } from "./cycles"
import { coherentEnergyV1, recommendedEnergyBand, energyBandMap } from "./coherent-energy"
import { markerOverlapResearch, visibleEnergyGuide } from "./marker-overlap"
import { evaluationRangeScopes, ENGINE_READY } from "./evaluation-range"

export function createPcgCore(payload, options = {}) {
  if (!payload) throw new Error("pcg-core: guide payload is required")
  const traces = options.traces || decodeAnalysisTraces(payload)
  const { engine, metricPayload, visualPayload } = evaluationRangeScopes(payload)
  const ctx = Object.freeze({
    payload: metricPayload,
    visualPayload,
    guidePayload: payload,
    engine,
    evaluationRange: engine.status === ENGINE_READY ? engine.selected : null,
    murmurTrace: traces.murmur || null,
    turbulentModel: options.turbulentModel || null,
    phaseReviewResolution: options.phaseReviewResolution || null,
    examTimingConsistency: options.examTimingConsistency || null,
    currentRecordingId: options.currentRecordingId || null,
    cache: {},
  })
  return {
    ctx,
    analysisQuality: () => analysisQuality(ctx),
    timingStats: () => timingStats(ctx),
    timingConsistency: () => timingConsistency(ctx),
    rhythmPatternFromTiming: (timing) => rhythmPatternFromTiming(ctx, timing),
    crossPositionTimingReview: () => crossPositionTimingReview(ctx),
    crossPositionTimingCorrection: () => crossPositionTimingCorrection(ctx),
    visualAcousticActivityStats: (strictEvidence = false) => visualAcousticActivityStats(ctx, strictEvidence),
    murmurEvidenceStats: () => murmurEvidenceStats(ctx),
    structuredTurbulenceEvidence: () => structuredTurbulenceEvidence(ctx),
    nrpeExperimentSchema: (activity) => nrpeExperimentSchema(ctx, activity),
    phaseRiskConfidence: () => phaseRiskConfidence(ctx),
    phaseReviewNeeded: () => phaseReviewNeeded(ctx),
    phaseAlternateActivity,
    intervalAt: (t) => intervalAt(ctx.visualPayload, ctx.phaseReviewResolution, t),
    buildCardiacReportEvidencePackage: (exportOptions = {}) => buildCardiacReportEvidencePackage(ctx, exportOptions),
    // Coherent Energy research comparison (dev.18; RESEARCH-ONLY — never feeds
    // Provisional Results or the export builder's released fields).
    coherentEnergyV1: (bandKey = "s100") => coherentEnergyV1(ctx, bandKey),
    // Marker-overlap recovery research check (handoff v0.8.3; RESEARCH-ONLY —
    // same guardrail as coherentEnergyV1).
    markerOverlapResearch: (bandKey = "s100") => markerOverlapResearch(ctx, bandKey),
    visibleEnergyGuide: (bandKey = "s100", energyGuideMode = "coherent", markerOverlapPreview = false) =>
      visibleEnergyGuide(ctx, bandKey, energyGuideMode, markerOverlapPreview),
    recommendedEnergyBand: () => recommendedEnergyBand(ctx),
    energyBandMap: () => energyBandMap(ctx),
  }
}

export * from "./constants"
export * from "./format"
export * from "./stats"
export * from "./traces"
export * from "./gate"
export * from "./cycles"
export * from "./energy"
export * from "./turbulence"
export * from "./analysis-quality"
export * from "./timing"
export * from "./evidence"
export * from "./phase"
export * from "./classifiers"
export * from "./export-package"
export * from "./coherent-energy"
export * from "./marker-overlap"
export * from "./evaluation-range"
